import { apiClient } from "@/api/api-client";
import { ApiResponse, PageContent } from "@/types/api-response";
import {
  FilterRequest,
  Product,
  ProductFormValues,
  ProductResponse,
  ProductSortOption,
  ProductUpdateValues,
  ProductVariant,
  TProductWithRelation,
  VariantStockSearchItem,
  VariantWithRelationResponse,
} from "@/types/product-type";
import {
  normalizeCreateValues,
  normalizeUpdateValues,
} from "@/utils/product-form-utils";
import { endpoints } from "./endpoints/endpoints";

// Create a new product
export async function createProduct(
  product: ProductFormValues
): Promise<ApiResponse<Product>> {
  const normalizedPayload = normalizeCreateValues(product);
  const response = await apiClient.post<
    Product,
    Omit<ProductFormValues, "colorValues" | "sizeValues">
  >("/products", normalizedPayload);
  return response;
}

export async function getProducts(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
  sort?: ProductSortOption;
}): Promise<ApiResponse<PageContent<Product>>> {
  const response = await apiClient.get<PageContent<Product>, object>(
    "/products",
    params || {}
  );
  return response;
}

// Get products with relations (variants and attributes)
export async function getProductsWithRelations(): Promise<
  ApiResponse<TProductWithRelation[]>
> {
  const response = await apiClient.get<TProductWithRelation[], object>(
    "/products"
  );
  return response;
}

// Get a single product by ID
export async function getProduct(id: string): Promise<ApiResponse<Product>> {
  try {
    const response = await apiClient.get<Product, void>(
      endpoints.product.get(id)
    );
    return response as ApiResponse<Product>;
  } catch (error) {
    console.error("Error getting message template:", error);
    throw error;
  }
}
export async function getProductWithRelation(
  id: string
): Promise<ApiResponse<TProductWithRelation>> {
  if (!id) {
    throw new Error("Product ID is required");
  }
  return await apiClient.get<TProductWithRelation, object>(
    `/products/${id}/relation`
  );
}

// Update an existing product
export async function updateProduct(
  id: string,
  product: ProductFormValues
): Promise<ApiResponse<Product>> {
  const normalizedPayload = normalizeUpdateValues(product);
  const response = await apiClient.put<Product, ProductUpdateValues>(
    `/products/${id}`,
    normalizedPayload
  );
  return response;
}

// Delete a product (soft delete)
export async function deleteProduct(id: string): Promise<ApiResponse<Product>> {
  const response = await apiClient.delete<Product>(`/products/${id}`);
  return response;
}

// Update product price
export async function updateProductPrice(
  productId: string,
  price: string
): Promise<ApiResponse<Product>> {
  const response = await apiClient.patch<Product, { price: string }>(
    `/products/${productId}`,
    { price }
  );
  return response;
}

// Bulk update product prices
export async function bulkUpdateProductPrices(
  products: { id: string; price: string }[]
): Promise<ApiResponse<Product[]>> {
  const promises = products.map((product) =>
    updateProductPrice(product.id, product.price)
  );
  const results = await Promise.all(promises);
  return {
    data: results.map((r) => r.data),
    success: true,
    statusText: "OK",
    message: "Bulk price update successful",
    status: 200,
  } as ApiResponse<Product[]>;
}

export async function createProductDefaultVariant(
  productId: string
): Promise<ApiResponse<ProductVariant>> {
  const response = await apiClient.post<ProductVariant, {}>(
    `/product-variants/${productId}/default`,
    {}
  );
  return response;
}

// Search products with pagination request
export async function searchProductsWithPagination(
  request: FilterRequest
): Promise<ApiResponse<PageContent<VariantStockSearchItem>>> {
  const response = await apiClient.post<
    PageContent<VariantStockSearchItem>,
    FilterRequest
  >("/products/search", request);
  return response;
}

// Search product variants with pagination request
export async function searchProductVariantsWithPagination(
  request: FilterRequest
): Promise<ApiResponse<PageContent<VariantStockSearchItem>>> {
  const response = await apiClient.post<
    PageContent<VariantStockSearchItem>,
    FilterRequest
  >("/product-variants/search", request);
  return response;
}

// Get product variants with stock
export async function getProductVariants(
  productId: string
): Promise<ApiResponse<VariantWithRelationResponse[]>> {
  const response = await apiClient.get<VariantWithRelationResponse[], void>(
    `/products/${productId}/variants`
  );
  return response;
}

// Check if a product code already exists
export async function checkProductCodeExists(
  code: string
): Promise<ApiResponse<boolean>> {
  const response = await apiClient.get<boolean, { code: string }>(
    `/products/check-code`,
    { code }
  );
  debugger;
  return response;
}
